import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { HierarchicalCostCentre } from '../cost-centres-hierarchical-table';

interface AddExistingCostCentreModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedNodeName: string;
  availableCostCentres: HierarchicalCostCentre[];
  onAdd: (costCentreIds: number[]) => void;
}

export function AddExistingCostCentreModal({
  open,
  onOpenChange,
  selectedNodeName,
  availableCostCentres,
  onAdd,
}: AddExistingCostCentreModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
    }
  }, [open]);

  const toggleSelection = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    onAdd(Array.from(selectedIds));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Existing Cost Centre</DialogTitle>
          <DialogDescription>
            Select cost centres that exist in this project but aren't in the structure
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] py-4">
          {availableCostCentres.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No available cost centres to add. All cost centres are already in the structure.
            </div>
          ) : (
            <div className="space-y-2">
              {availableCostCentres.map(cc => (
                <div
                  key={cc.id}
                  className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => toggleSelection(cc.id)}
                >
                  <Checkbox
                    checked={selectedIds.has(cc.id)}
                    onCheckedChange={() => toggleSelection(cc.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{cc.name}</span>
                      {cc.billable && (
                        <span className="h-2 w-2 rounded-full bg-success-500" title="Billable" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{cc.code}</span>
                      <Badge variant="secondary" className="text-xs">
                        {cc.type}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <div className="text-sm text-muted-foreground">
            Adding to: <span className="font-medium text-foreground">{selectedNodeName}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={selectedIds.size === 0}>
              Add Selected ({selectedIds.size})
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
